import { Connection } from '@lucidtech/las-sdk-browser';
import { Key, memo, useCallback, useEffect, useState } from 'react';
import { ComboBoxProps } from 'react-aria-components';

import { merge } from '@/components';
import {
  BasicTooltip,
  Button,
  Flex,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Save,
  Select,
  SelectValue,
  Separator,
  Text,
} from '@/components/core';
import { ChevronIcon, ExcelTableIcon, RotateIcon } from '@/components/icons';
import { useTriggerStore } from '@/store';

import { GraphObject } from '../client';
import { useGraphClient } from '../hooks';

interface TableSelectorProps<T extends object> extends Omit<ComboBoxProps<T>, 'children'> {
  connection?: Connection;
}

const ITableSelector = <T extends object>({ connection }: TableSelectorProps<T>) => {
  const graphClient = useGraphClient(connection);
  const siteId = useTriggerStore((state) => state.action.config.siteId) as string | undefined;
  const driveId = useTriggerStore((state) => state.action.config.driveId) as string | undefined;
  const driveItemId = useTriggerStore((state) => state.action.config.driveItemId) as string | undefined;
  const currentTableId = useTriggerStore((state) => state.action.config?.tableId) as string | undefined;
  const updateAction = useTriggerStore((state) => state.updateAction);
  const [isLoading, setIsLoading] = useState(false);
  const [tables, setTables] = useState<GraphObject[]>([]);

  const loadTables = useCallback(async () => {
    if (driveItemId && driveId) {
      setIsLoading(true);

      try {
        const worksheets = (await graphClient.getWorksheets(siteId, driveId, driveItemId)) ?? [];

        const tables = [];
        for (const worksheet of worksheets) {
          if (worksheet.id != null) {
            const res = await graphClient.getTables(siteId, driveId, driveItemId, worksheet.id);
            tables.push(...res);
          }
        }
        setTables(tables);
      } finally {
        setIsLoading(false);
      }
    } else {
      setTables([]);
    }
  }, [driveId, driveItemId, graphClient, siteId]);

  useEffect(() => {
    loadTables();
  }, [loadTables]);

  const onSelectionChange = useCallback(
    (key: Key | null) => {
      const tableId = key?.toString() || null;
      updateAction((state) => ({ config: { ...state.config, fieldMapping: {}, tableId } }));
    },
    [updateAction]
  );

  return (
    <Flex col className="gap-xs">
      {tables.length ? (
        <>
          <Label isRequired>Table</Label>
          <Select
            value={currentTableId}
            onChange={onSelectionChange}
            placeholder="Select table"
            isDisabled={!driveItemId}
          >
            <div className="flex grow-0">
              <Button variant="secondary">
                <Text slot="description" className="gap-sm flex grow items-center">
                  {isLoading ? <RotateIcon rotation="cw" className={merge('size-3!', 'animate-spin')} /> : null}
                  <ExcelTableIcon className="size-4" />
                  <SelectValue />
                </Text>
                <ChevronIcon rotation="down" />
              </Button>
            </div>
            <Popover>
              <Flex col className="gap-xs">
                <ListBox>
                  {tables.map((table) => (
                    <ListBoxItem id={table.id} key={table.id} className="py-0" textValue={table.name}>
                      <Text>{table.name}</Text>
                    </ListBoxItem>
                  ))}
                </ListBox>

                <Separator orientation="horizontal" />

                <Button onPress={loadTables}>
                  <RotateIcon rotation="cw" className={merge('size-3!', 'text-gray-600!')} />
                  <Text>Refresh</Text>
                  <BasicTooltip>
                    <Text>Please allow a few minutes for changes you make to your Excel workbook to show up here.</Text>
                  </BasicTooltip>
                </Button>
              </Flex>
            </Popover>
          </Select>
        </>
      ) : driveItemId && driveId ? (
        <Save message="Loading table" />
      ) : null}
    </Flex>
  );
};

export const TableSelector = memo(ITableSelector) as typeof ITableSelector;
